// features/voicemail/components/ConfirmDeleteVoicemailDialog.tsx
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@core-ui/Components/ui/dialog';
import { Button } from '@core-ui/Components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  title: string;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
  disableCloseOnLoading?: boolean;
}

export default function ConfirmDeleteVoicemailDialog ({
  open,
  title,
  loading = false,
  onCancel,
  onConfirm,
  disableCloseOnLoading = true,
}: Props): React.JSX.Element {
  const { t } = useTranslation();

  const handleOpenChange = (isOpen: boolean): void => {
    if (disableCloseOnLoading && loading) {
      return;
    }
    if (!isOpen) {
      onCancel();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-lg rounded-2xl shadow-lg border border-border bg-card text-foreground p-6"
        onEscapeKeyDown={(e) => {
          if (disableCloseOnLoading && loading) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          if (disableCloseOnLoading && loading) {
            e.preventDefault();
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !loading) {
            e.preventDefault();
            void onConfirm();
          }
        }}
      >
        <DialogHeader className="mb-2">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-destructive/30 bg-destructive/10 text-destructive">
              <AlertTriangle className="h-5 w-5" />
            </span>
            <DialogTitle className="text-lg font-semibold">
              {t('voice.voicemails.delete.title', 'Delete voicemail')}
            </DialogTitle>
          </div>
        </DialogHeader>

        <DialogDescription asChild>
          <div className="text-sm leading-relaxed text-muted-foreground">
            <Trans
              i18nKey="voice.voicemails.delete.body"
              components={{ strong: <span className="font-semibold text-foreground" /> }}
              values={{ title }}
              defaults="Are you sure you want to delete <strong>{{title}}</strong>? This action cannot be undone."
            />
          </div>
        </DialogDescription>

        <div className="flex justify-end gap-2 mt-6">
          <Button
            data-qa-id="cancel-delete-vm-button"
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            className="border border-border text-foreground hover:bg-muted"
            autoFocus
          >
            {t('adminConsole.common.cancel', 'Cancel')}
          </Button>

          <Button
            data-qa-id="delete-vm-button"
            type="button"
            variant="destructive"
            onClick={() => {
              void onConfirm();
            }}
            disabled={loading}
            className="text-primary-foreground"
            aria-label={t('voice.voicemails.delete.ariaConfirm', 'Confirm delete voicemail')}
          >
            {loading ? t('voice.voicemails.delete.deleting', 'Deleting…') : t('voice.voicemails.delete.deleteButton', 'Delete')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
